import { useEffect, useMemo, useRef, useState } from 'react';

import useDebounced from '../../../hooks/useDebounced';
import InputIconByKey from './InputIconByKey';
import RequiredAsteriskTooltip from './RequiredAsteriskTooltip';
import SvgIcon from '../../SvgIcon/SvgIcon';
import {
  IconKey,
  RESTAURANT_MENU_ICON_OPTIONS,
  RestaurantIconOption,
} from '../../../types/system/icons';

interface IconPickerFieldProps {
  label: string;
  name: string;
  /** `IconKey` seleccionado, o cadena vacía cuando no hay icono. */
  value: string;
  onChange: (value: string) => void;
  options?: RestaurantIconOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  /** Icono del recuadro izquierdo (decorativo). */
  iconKey?: IconKey;
}

const IconPickerField: React.FC<IconPickerFieldProps> = ({
  label,
  name,
  value,
  onChange,
  options = RESTAURANT_MENU_ICON_OPTIONS,
  placeholder = 'Sin icono',
  searchPlaceholder = 'Buscar icono...',
  error,
  required,
  disabled,
  iconKey = 'tag',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 200);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => options.find((option) => option.key === value) ?? null,
    [options, value]
  );

  const filtered = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    if (!term) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(term) ||
        option.key.toLowerCase().includes(term) ||
        option.keywords.toLowerCase().includes(term)
    );
  }, [options, debouncedSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || disabled) return;
    const timeout = window.setTimeout(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [isOpen, disabled]);

  const choose = (next: string) => {
    onChange(next);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className="mb-5">
      {label && (
        <label className="mb-2.5 flex items-center gap-1 text-sm font-medium text-black dark:text-white">
          {label}
          {required && <RequiredAsteriskTooltip delay={400} />}
        </label>
      )}

      <div className="relative min-w-0" ref={containerRef}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
          className={`bg-gray-1 flex w-full min-w-0 items-center overflow-hidden rounded-xl border border-stroke text-left transition-all duration-200 focus-within:border-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/10 dark:border-strokedark dark:bg-meta-4 ${
            disabled ? 'cursor-not-allowed opacity-70' : ''
          }`}
        >
          <span className="hidden h-12.5 w-12 shrink-0 items-center justify-center border-r border-stroke bg-gray-2 text-primary dark:border-strokedark dark:bg-boxdark-2 sm:flex">
            {selected ? (
              <InputIconByKey
                iconKey={selected.key}
                className="h-5 w-5 text-primary"
              />
            ) : (
              <InputIconByKey
                iconKey={iconKey}
                className="h-5 w-5 text-bodydark2"
              />
            )}
          </span>

          <span className="flex min-w-0 flex-1 items-center gap-2 px-4 py-3.5 text-sm text-black dark:text-white">
            {selected && (
              <InputIconByKey
                iconKey={selected.key}
                className="h-4 w-4 shrink-0 text-primary sm:hidden"
              />
            )}
            <span className="truncate">
              {selected ? selected.label : placeholder}
            </span>
          </span>

          <span className="flex shrink-0 items-center justify-center pr-4">
            <SvgIcon
              name="chevron-down"
              className={`h-5 w-5 transform transition-transform duration-300 ${
                isOpen ? 'rotate-180 text-primary' : 'text-body dark:text-bodydark2'
              }`}
            />
          </span>
        </button>

        {isOpen && (
          <div className="absolute z-999999 mt-1 w-full overflow-hidden rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
            <input
              ref={searchInputRef}
              type="text"
              name={name}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="bg-gray-1 w-full border-b border-stroke px-4 py-3 text-sm text-black outline-none transition-all duration-200 placeholder:text-body focus:border-primary dark:border-strokedark dark:bg-meta-4 dark:text-white dark:placeholder:text-bodydark2"
            />

            <div className="max-h-64 overflow-y-auto p-2">
              <button
                type="button"
                onClick={() => choose('')}
                className={`mb-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                  !value
                    ? 'bg-primary/10 text-primary'
                    : 'text-body hover:bg-gray-2 dark:text-bodydark2 dark:hover:bg-meta-4'
                }`}
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-md border border-dashed border-stroke dark:border-strokedark">
                  <SvgIcon name="close" className="h-3 w-3" />
                </span>
                Sin icono
              </button>

              <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4">
                {filtered.map((option) => {
                  const active = option.key === value;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      title={option.label}
                      onClick={() => choose(option.key)}
                      className={`flex flex-col items-center gap-1 rounded-lg border px-1.5 py-2 text-center transition-colors ${
                        active
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-transparent text-body hover:border-stroke hover:bg-gray-2 dark:text-bodydark2 dark:hover:bg-meta-4'
                      }`}
                    >
                      <InputIconByKey
                        iconKey={option.key}
                        className={`h-5 w-5 ${
                          active ? 'text-primary' : 'text-black dark:text-white'
                        }`}
                      />
                      <span className="line-clamp-2 text-[10px] leading-tight">
                        {option.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              {filtered.length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-body dark:text-bodydark2">
                  No se encontraron iconos
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {error && <p className="mt-2 text-sm text-danger">{error}</p>}
    </div>
  );
};

export default IconPickerField;
